import { ReactNode, useEffect, useRef } from 'react';
import './events.web-payment-failure';
import type { useDialog } from '@zep/dialog';
import { trackEvent } from '@zep/logger/tracking';
import { PaymentFailureMetadata } from './paymentFailure';
import { isPaymentCancellation, toFailureImpression } from './paymentFailureDecision';
type Props = {
  children: ReactNode;
  metadata: PaymentFailureMetadata;
  as?: 'span' | 'div';
};
/** A fresh dialog/page mount is a fresh exposure; rerenders of it are not. */
export function PaymentFailureContent({
  children,
  metadata,
  as: Element = 'span'
}: Props) {
  const recorded = useRef(false);
  const latest = useRef(metadata);
  latest.current = metadata;
  useEffect(() => {
    // 마운트가 곧 노출이다(DialogProvider 는 alert 호출 시 렌더한다). 백그라운드 탭이면 돌아왔을 때 센다.
    const record = () => {
      if (recorded.current || document.visibilityState !== 'visible') return;
      recorded.current = true;
      const payload = toFailureImpression(latest.current);
      if (payload) trackEvent('web_payment_failure_ui_impression', payload);
    };
    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, []);
  return <Element data-sentry-element="Element" data-sentry-component="PaymentFailureContent" data-sentry-source-file="PaymentFailureContent.tsx">{children}</Element>;
}
/** Wrap only payment notices. Payment behavior and close promises remain unchanged. */
export function createPaymentFailureDialog(dialog: ReturnType<typeof useDialog>, metadata: PaymentFailureMetadata, error?: unknown) {
  const wrap = <T extends {
    title: ReactNode;
    content?: ReactNode;
  },>(props: T): T => {
    if (isPaymentCancellation(error) || isPaymentCancellation(metadata)) return props;
    const key = props.content == null || typeof props.content === 'string' && props.content.trim() === '' ? 'title' : 'content';
    return {
      ...props,
      [key]: <PaymentFailureContent metadata={metadata}>
          {props[key]}
        </PaymentFailureContent>
    };
  };
  return {
    alert: (props: Parameters<typeof dialog.alert>[0]) => dialog.alert(wrap(props)),
    confirm: (props: Parameters<typeof dialog.confirm>[0]) => dialog.confirm(wrap(props))
  };
}